/**
 * Central read for the four `kitchen.*` app_settings that drive KDS behavior:
 *   - kitchen.mode                (kds | kot_only; legacy 'hybrid' parsed as KDS)
 *   - kitchen.urgency_minutes
 *   - kitchen.stale_minutes
 *   - kitchen.recall_window_seconds
 *
 * All components should read from this hook rather than rolling their own
 * parse, so mode changes land everywhere consistently (nav, KDS, fire flow).
 *
 * Note: 'hybrid' is preserved in the union for backward-compatibility with
 * any rows persisted before the admin UI was simplified to KDS / KOT-only.
 * It behaves identically to 'kds' (see `isKDSEnabled`).
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'

export type KitchenMode = 'kds' | 'kot_only' | 'hybrid'

export interface KitchenSettings {
  mode: KitchenMode
  urgencyMinutes: number
  staleMinutes: number
  recallWindowSeconds: number
  /** Per-order-type target prep minutes (drives the urgency colour escalation
   * per ticket). Missing types fall back to `urgencyMinutes` via prepTargetFor. */
  prepTargets: Record<string, number>
  /** Target prep minutes for an order type — per-type override or global default. */
  prepTargetFor: (orderType: string | undefined) => number
  /** True while the initial fetch is in flight — UI should avoid committing yet. */
  isLoading: boolean
}

export const KITCHEN_SETTINGS_DEFAULTS: Omit<
  KitchenSettings,
  'isLoading' | 'prepTargets' | 'prepTargetFor'
> = {
  mode: 'kds',
  urgencyMinutes: 15,
  staleMinutes: 120,
  recallWindowSeconds: 300,
}

function parseMode(v: unknown): KitchenMode | undefined {
  if (typeof v !== 'string') return undefined
  const s = v.trim().toLowerCase()
  if (s === 'kds' || s === 'kot_only' || s === 'hybrid') return s
  return undefined
}

function parseNumber(v: unknown, fallback: number, lo: number, hi: number): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN
  if (!Number.isFinite(n)) return fallback
  return Math.min(hi, Math.max(lo, Math.round(n)))
}

/** Parse the kitchen.prep_target_minutes setting (a JSON object keyed by order
 * type, e.g. {"dine_in":15,"takeout":12,"delivery":20}). Tolerates the value
 * arriving already-parsed (object) or as a JSON string; clamps each to 1–240. */
function parsePrepTargets(v: unknown): Record<string, number> {
  let obj: Record<string, unknown> | undefined
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    obj = v as Record<string, unknown>
  } else if (typeof v === 'string' && v.trim() !== '') {
    try {
      const p = JSON.parse(v)
      if (p && typeof p === 'object' && !Array.isArray(p)) obj = p as Record<string, unknown>
    } catch {
      /* ignore malformed */
    }
  }
  const out: Record<string, number> = {}
  if (obj) {
    for (const [k, val] of Object.entries(obj)) {
      const n = typeof val === 'number' ? val : typeof val === 'string' ? Number(val) : NaN
      if (Number.isFinite(n)) out[k] = Math.min(240, Math.max(1, Math.round(n)))
    }
  }
  return out
}

/**
 * Shared React Query cache key — deliberately the SAME key the rest of the
 * app uses for `getAllSettings()` so any admin save (receipt, financial,
 * kitchen, order-types, appearance) invalidates this hook too and every
 * consumer re-renders in lockstep.
 */
export const KITCHEN_SETTINGS_QUERY_KEY = ['settings', 'all'] as const

export function useKitchenSettings(): KitchenSettings {
  const { data, isLoading } = useQuery({
    queryKey: KITCHEN_SETTINGS_QUERY_KEY,
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  })

  return useMemo<KitchenSettings>(() => {
    const raw = (data?.data as Record<string, unknown> | undefined) ?? {}
    const urgencyMinutes = parseNumber(
      raw['kitchen.urgency_minutes'],
      KITCHEN_SETTINGS_DEFAULTS.urgencyMinutes,
      1,
      240,
    )
    const prepTargets = parsePrepTargets(raw['kitchen.prep_target_minutes'])
    return {
      mode: parseMode(raw['kitchen.mode']) ?? KITCHEN_SETTINGS_DEFAULTS.mode,
      urgencyMinutes,
      staleMinutes: parseNumber(raw['kitchen.stale_minutes'], KITCHEN_SETTINGS_DEFAULTS.staleMinutes, 15, 1440),
      recallWindowSeconds: parseNumber(
        raw['kitchen.recall_window_seconds'],
        KITCHEN_SETTINGS_DEFAULTS.recallWindowSeconds,
        0,
        3600,
      ),
      prepTargets,
      prepTargetFor: (orderType) =>
        (orderType && prepTargets[orderType]) || urgencyMinutes,
      isLoading,
    }
  }, [data, isLoading])
}

/** Convenience — `/kitchen` nav visibility and screen access. */
export function isKDSEnabled(mode: KitchenMode): boolean {
  return mode === 'kds' || mode === 'hybrid'
}
